import { useState, useEffect } from "react";
import { Card } from "@shadcn-in-nx/ui/card";
import { Button as UiButton } from "@shadcn-in-nx/ui/button";
import { Bluelagoon } from "@bluelagoon";
import { BuyCreditsModal } from "../modals/BuyCreditsModal";
import { AutoReloadSettingsModal } from "../modals/AutoReloadSettingsModal";

export function CreditBalanceCard({
    platform,
    platformName,
    currentBalance,
    paymentMethod,
    onRefresh,
    onPurchase,
    highlight = false,
    t,
}) {
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [showBuyModal, setShowBuyModal] = useState(false);
    const [showAutoReloadModal, setShowAutoReloadModal] = useState(false);
    const [autoReloadSettings, setAutoReloadSettings] = useState(null);

    useEffect(() => {
        loadAutoReloadSettings();
    }, [platform]);

    const loadAutoReloadSettings = async () => {
        try {
            setLoading(true);
            const response = await Bluelagoon.getAutoChargeSettings({
                platform,
            });

            if (response?.data?.data) {
                setAutoReloadSettings(response.data.data);
            }
        } catch (error) {
            console.error("Error loading auto-reload settings:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleModalSuccess = async () => {
        setRefreshing(true);
        try {
            await loadAutoReloadSettings();
            if (onRefresh) {
                await onRefresh();
            }
            // Wait a bit to ensure data has loaded
            await new Promise(resolve => setTimeout(resolve, 1000));
        } finally {
            setRefreshing(false);
        }
    };

    const formatBalance = (balance) => {
        if (platform === "agentui") {
            return `${balance.toFixed(2)} credits`;
        }
        return `$${balance.toFixed(2)}`;
    };

    const formatThreshold = (value) => {
        // Always show USD for auto-reload thresholds
        return `$${value}`;
    };

    return (
        <>
            <Card className={`p-8 transition-all duration-500 ${
                highlight
                    ? "ring-4 ring-blue-500 ring-offset-2 shadow-2xl bg-blue-50"
                    : ""
            }`}>
                <div className="mb-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">
                        {t("billing.creditBalance") || "Credit balance"} -{" "}
                        {platformName}
                    </h2>
                    <p className="text-gray-600">
                        {t("billing.creditBalanceDescription") ||
                            "Your credit balance will be consumed with API, Claude Code and Workbench usage. You can buy credits directly or set up auto-reload thresholds."}
                    </p>
                </div>

                <div className="flex flex-col md:flex-row gap-6 items-start">
                    {/* Balance Display */}
                    <div className="flex-shrink-0 bg-gray-100 rounded-xl p-8 min-w-[280px] relative">
                        {refreshing && (
                            <div className="absolute inset-0 bg-gray-100/80 rounded-xl flex items-center justify-center backdrop-blur-sm">
                                <div className="flex flex-col items-center gap-2">
                                    <svg
                                        className="w-8 h-8 text-gray-600 animate-spin"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        />
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        />
                                    </svg>
                                    <span className="text-sm text-gray-600 font-medium">
                                        {t("billing.refreshing") || "Refreshing..."}
                                    </span>
                                </div>
                            </div>
                        )}
                        <div className="text-5xl font-bold text-gray-900 mb-2">
                            {formatBalance(currentBalance)}
                        </div>
                        <div className="text-gray-600">
                            {t("billing.remainingBalance") ||
                                "Remaining Balance"}
                        </div>
                    </div>

                    {/* Right side info */}
                    <div className="flex-1 space-y-4">
                        {/* Payment Method */}
                        {paymentMethod && (
                            <div className="flex items-center gap-3 text-gray-700">
                                <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                                    />
                                </svg>
                                <span className="text-sm">
                                    {t("billing.chargedTo") || "Charged to"}{" "}
                                    <span className="font-medium">
                                        {paymentMethod.brand} ••••{" "}
                                        {paymentMethod.last4}
                                    </span>
                                </span>
                            </div>
                        )}

                        {/* Buy Credits Button */}
                        <div>
                            <UiButton
                                onClick={() => setShowBuyModal(true)}
                                className="bg-gray-900 hover:bg-gray-800 text-white"
                            >
                                {t("billing.buyCredits") || "Buy credits"}
                            </UiButton>
                        </div>

                        {/* Auto-reload notification */}
                        {!loading && autoReloadSettings?.autoChargeEnabled && (
                            <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                                <svg
                                    className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                    />
                                </svg>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm text-gray-900">
                                        {t("billing.autoReloadEnabled") ||
                                            "Auto reload is enabled."}{" "}
                                        {t("billing.autoReloadWillReload") ||
                                            "We will reload to"}{" "}
                                        <span className="font-medium">
                                            {formatThreshold(
                                                autoReloadSettings.autoChargeAmount
                                            )}
                                        </span>{" "}
                                        {t("billing.autoReloadWhenBalance") ||
                                            "when the balance reaches"}{" "}
                                        <span className="font-medium">
                                            {formatThreshold(
                                                autoReloadSettings.autoChargeThreshold
                                            )}
                                        </span>
                                        .
                                    </p>
                                </div>
                                <UiButton
                                    onClick={() => setShowAutoReloadModal(true)}
                                    variant="ghost"
                                    size="sm"
                                    className="flex-shrink-0 text-gray-900 hover:bg-blue-100"
                                >
                                    {t("billing.edit") || "Edit"}
                                </UiButton>
                            </div>
                        )}

                        {/* No auto-reload notification */}
                        {!loading &&
                            !autoReloadSettings?.autoChargeEnabled &&
                            paymentMethod && (
                                <div className="flex items-start gap-3 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                                    <svg
                                        className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-gray-700">
                                            {t("billing.autoReloadDisabled") ||
                                                "Auto reload is disabled. Enable it to avoid service disruptions."}
                                        </p>
                                    </div>
                                    <UiButton
                                        onClick={() => setShowAutoReloadModal(true)}
                                        variant="ghost"
                                        size="sm"
                                        className="flex-shrink-0 text-gray-900 hover:bg-gray-100"
                                    >
                                        {t("billing.setup") || "Set up"}
                                    </UiButton>
                                </div>
                            )}
                    </div>
                </div>
            </Card>

            <BuyCreditsModal
                isOpen={showBuyModal}
                onClose={() => setShowBuyModal(false)}
                platform={platform}
                platformName={platformName}
                currentBalance={currentBalance}
                paymentMethod={paymentMethod}
                onSuccess={handleModalSuccess}
                onPurchase={onPurchase}
                t={t}
            />

            <AutoReloadSettingsModal
                isOpen={showAutoReloadModal}
                onClose={() => setShowAutoReloadModal(false)}
                platform={platform}
                platformName={platformName}
                paymentMethod={paymentMethod}
                onSuccess={handleModalSuccess}
                t={t}
            />
        </>
    );
}
